/**
 * Footbar Bridge for Dawn Variant Picker
 * Misell theme - high-variant support
 *
 * Syncs footbar Dawn variant picker changes to update the hidden variant ID input
 * so cart add works correctly from the footbar.
 */
(function() {
  'use strict';

  let productJsonCache = null;
  let requestSerial = 0;
  let isInitialized = false;

  // Wait for DOM and pub-sub to be ready
  function initFootbarBridge() {
    if (isInitialized) return;
    const footbarPicker = document.querySelector('.js-footbar-product variant-selects');
    if (!footbarPicker) {
      return;
    }
    isInitialized = true;

    // Load product JSON asynchronously
    loadProductJson().then(productJson => {
      if (!productJson) {
        return;
      }

      productJsonCache = productJson;

      // Subscribe to option value selection changes
      const canUsePubSub =
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.optionValueSelectionChange;
      if (canUsePubSub) {
        subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function(event) {
          handleFootbarOptionChange(event, productJson, footbarPicker);
        });
      } else {
        // Fallback: listen to change events directly
        footbarPicker.addEventListener('change', function(e) {
          if (e.target.type === 'hidden') return;
          handleFootbarChangeEvent(e, productJson, footbarPicker);
        });
      }
    });
  }

  async function loadProductJson() {
    // Try to get product JSON from main product element
    const mainProduct = document.querySelector('[data-js-product-clone-id="footbar"]');
    if (mainProduct) {
      const jsonAttr = mainProduct.getAttribute('data-json-product');
      if (jsonAttr) {
        try {
          // Check if it's inline JSON or a URL
          if (jsonAttr.startsWith('{')) {
            return JSON.parse(jsonAttr);
          } else {
            // It's a URL, fetch the JSON
            console.log('[FootbarBridge] Fetching product JSON from:', jsonAttr);
            const response = await fetch(jsonAttr);
            return await response.json();
          }
        } catch (e) {
          console.error('[FootbarBridge] Error loading product JSON:', e);
        }
      }
    }

    // Fallback: try to get from footbar's data-selected-variant
    const footbarVariantJson = document.querySelector('.js-footbar-product [data-selected-variant]');
    if (footbarVariantJson) {
      try {
        const variant = JSON.parse(footbarVariantJson.textContent);
        // Return minimal structure with current variant
        return { variants: [variant], currentVariant: variant };
      } catch (e) {
        console.error('[FootbarBridge] Error parsing footbar variant JSON:', e);
      }
    }

    return null;
  }

  function handleFootbarOptionChange(event, productJson, footbarPicker) {
    const { data } = event;
    if (!data || !data.event) return;

    // Only handle changes from footbar picker
    if (!footbarPicker.contains(data.event.target)) {
      return;
    }

    // For high-variant products, use Section Rendering API to get variant
    const productUrl = footbarPicker.dataset.productUrl;
    if (productUrl && data.selectedOptionValues) {
      fetchVariantFromSectionAPI(productUrl, data.selectedOptionValues, data.selectedOptions, footbarPicker);
    } else {
      // Fallback: try local lookup (works for < 100 variants)
      const variant = findVariantByOptions(productJson, data.selectedOptions);
      if (variant) {
        updateMainProductVariant(variant);
        updateFootbarVariant(variant, footbarPicker);
      }
    }
  }

  async function fetchVariantFromSectionAPI(productUrl, optionValueIds, selectedOptions, footbarPicker) {
    const params = [];
    const requestId = ++requestSerial;

    // Disable Add to Cart button during fetch to prevent timing issues
    const footbarProduct = footbarPicker.closest('.js-footbar-product');
    const addToCartBtn = footbarProduct?.querySelector('[data-js-product-button-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.setAttribute('disabled', 'disabled');
      addToCartBtn.classList.add('loading');
    }

    // Use option_values if available (Dawn v15+ pattern)
    const hasAllOptionValueIds = optionValueIds.length && optionValueIds.every(Boolean);
    if (hasAllOptionValueIds) {
      params.push(`option_values=${optionValueIds.join(',')}`);
    } else {
      // Fallback: send option1/2/3 params
      selectedOptions.forEach((opt) => {
        if (opt.position) {
          params.push(`option${opt.position}=${encodeURIComponent(opt.value)}`);
        }
      });
    }

    // Use main-product section to get variant JSON
    params.push('section_id=main-product');

    const requestUrl = `${productUrl}?${params.join('&')}`;
    console.log('[FootbarBridge] Fetching variant from:', requestUrl);

    try {
      const response = await fetch(requestUrl);
      const html = await response.text();
      if (requestId !== requestSerial) {
        console.log('[FootbarBridge] Stale response ignored');
        return;
      }

      // Parse the response to get variant JSON
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Try to find variant JSON in response
      const variantJson = doc.querySelector('[data-selected-variant]');
      if (variantJson) {
        const variant = JSON.parse(variantJson.textContent);
        if (requestId !== requestSerial) return;
        console.log('[FootbarBridge] Found variant via API:', variant.id, variant.title);
        updateMainProductVariant(variant);
        updateFootbarVariant(variant, footbarPicker);
        return;
      }

      // Alternative: parse variant from URL or input
      const variantInput = doc.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        const variantId = parseInt(variantInput.value, 10);
        if (requestId !== requestSerial) return;
        console.log('[FootbarBridge] Found variant ID via input:', variantId);
        updateFootbarVariantById(variantId, footbarPicker);
        return;
      }

      console.log('[FootbarBridge] Could not find variant in API response');
      // Re-enable button even if variant not found
      if (addToCartBtn) {
        addToCartBtn.removeAttribute('disabled');
        addToCartBtn.classList.remove('loading');
      }
    } catch (error) {
      console.error('[FootbarBridge] Error fetching variant:', error);
      // Re-enable button on error
      if (addToCartBtn) {
        addToCartBtn.removeAttribute('disabled');
        addToCartBtn.classList.remove('loading');
      }
    }
  }

  function updateFootbarVariantById(variantId, footbarPicker) {
    // Update the hidden input directly with variant ID
    const footbarFormVariantInput = document.getElementById('footbar-variant-id-input');
    if (footbarFormVariantInput) {
      console.log('[FootbarBridge] Updating #footbar-variant-id-input to:', variantId);
      footbarFormVariantInput.value = variantId;
    }

    // Update Dawn hidden input
    const dawnInput = footbarPicker.querySelector('[data-dawn-variant-id]');
    if (dawnInput) {
      dawnInput.value = variantId;
    }

    // Update all input[name="id"] in footbar form
    const footbarForm = document.getElementById('footbar-product-form');
    if (footbarForm) {
      footbarForm.querySelectorAll('input[name="id"]').forEach(input => {
        input.value = variantId;
      });
    }

    // Re-enable footbar add to cart button after variant ID update
    const footbarProduct = footbarPicker.closest('.js-footbar-product');
    if (footbarProduct) {
      const addToCartBtn = footbarProduct.querySelector('[data-js-product-button-add-to-cart]');
      if (addToCartBtn) {
        addToCartBtn.removeAttribute('disabled');
        addToCartBtn.classList.remove('loading');
      }
    }
  }

  function handleFootbarChangeEvent(event, productJson, footbarPicker) {
    // Get selected options from footbar picker
    const selectedOptions = getSelectedOptionsFromPicker(footbarPicker);
    console.log('[FootbarBridge] Fallback - Selected options:', selectedOptions);

    // Find matching variant
    const variant = findVariantByOptions(productJson, selectedOptions);
    if (variant) {
      console.log('[FootbarBridge] Found variant:', variant.id, variant.title);
      updateMainProductVariant(variant);
      updateFootbarVariant(variant, footbarPicker);
    }
  }

  function getSelectedOptionsFromPicker(picker) {
    const options = [];

    // Check selects
    picker.querySelectorAll('select').forEach(select => {
      const position = parseInt(select.dataset.optionPosition || 0, 10);
      if (select.selectedOptions[0]) {
        options.push({
          name: select.name,
          position: position,
          value: select.selectedOptions[0].value
        });
      }
    });

    // Check radio inputs
    picker.querySelectorAll('input[type="radio"]:checked').forEach(input => {
      const position = parseInt(input.dataset.optionPosition || 0, 10);
      options.push({
        name: input.dataset.optionName || input.name,
        position: position,
        value: input.value
      });
    });

    return options.sort((a, b) => a.position - b.position);
  }

  function findVariantByOptions(productJson, selectedOptions) {
    if (!productJson) return null;

    const variants = productJson.variants;
    if (!variants || !variants.length) return null;

    // Build option values array sorted by position
    const sortedOptions = [...selectedOptions].sort((a, b) => a.position - b.position);
    const optionValues = sortedOptions.map(opt => opt.value);

    console.log('[FootbarBridge] Looking for variant with options:', optionValues);

    // Find variant matching all options
    return variants.find(variant => {
      return optionValues.every((value, index) => {
        const optionKey = `option${index + 1}`;
        return variant[optionKey] === value;
      });
    });
  }

  function updateMainProductVariant(variant) {
    // Update main product's hidden variant input
    const mainProduct = document.querySelector('[data-js-product-clone-id="footbar"]');
    if (!mainProduct) {
      console.log('[FootbarBridge] Main product element not found');
      return;
    }

    // Update legacy variant input
    const variantInput = mainProduct.querySelector('[data-js-product-variant-input]');
    if (variantInput) {
      console.log('[FootbarBridge] Updating main product variant input to:', variant.id);
      variantInput.value = variant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update Dawn variant input if present
    const dawnInput = mainProduct.querySelector('input[data-dawn-variant-id]');
    if (dawnInput) {
      console.log('[FootbarBridge] Updating main product Dawn input to:', variant.id);
      dawnInput.value = variant.id;
      dawnInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update hidden id inputs
    mainProduct.querySelectorAll('input[name="id"][type="hidden"]').forEach(input => {
      input.value = variant.id;
    });

    // Update legacy variant select if it exists and has the option (may not for 250+ variants)
    const variantSelect = mainProduct.querySelector('[data-js-product-variants]');
    if (variantSelect && variantSelect.querySelector(`option[value="${variant.id}"]`)) {
      variantSelect.value = variant.id;
    }

    // Update product-variant-id attribute
    mainProduct.setAttribute('data-product-variant-id', variant.id);

    // Trigger legacy theme variant switch if available
    if (typeof jQueryTheme !== 'undefined') {
      const $mainProduct = jQueryTheme(mainProduct);
      // Update button state based on availability
      const $addToCartBtn = $mainProduct.find('[data-js-product-button-add-to-cart]');
      if ($addToCartBtn.length) {
        if (variant.available) {
          $addToCartBtn.removeAttr('disabled').attr('data-button-status', '');
        } else {
          $addToCartBtn.attr('disabled', 'disabled').attr('data-button-status', 'sold-out');
        }
      }
    }
  }

  function updateFootbarVariant(variant, footbarPicker) {
    if (!footbarPicker) return;

    console.log('[FootbarBridge] Updating footbar variant to:', variant.id);

    // Update footbar's hidden variant input (inside variant-selects)
    const footbarVariantInput = footbarPicker.querySelector('[data-dawn-variant-id]');
    if (footbarVariantInput) {
      console.log('[FootbarBridge] Updating footbar Dawn input');
      footbarVariantInput.value = variant.id;
    }

    // Update footbar form's hidden input (name="id") - this is the one jQuery serializeArray uses
    // Priority: Use the direct form input by ID
    const footbarFormVariantInput = document.getElementById('footbar-variant-id-input');
    if (footbarFormVariantInput) {
      console.log('[FootbarBridge] Updating #footbar-variant-id-input to:', variant.id);
      footbarFormVariantInput.value = variant.id;
    }

    // Also update any other input[name="id"] in the footbar form
    const footbarForm = document.getElementById('footbar-product-form');
    if (footbarForm) {
      footbarForm.querySelectorAll('input[name="id"]').forEach(input => {
        console.log('[FootbarBridge] Updating form input[name="id"] to:', variant.id);
        input.value = variant.id;
      });
    }

    // Update footbar's selected variant JSON
    const variantJson = footbarPicker.querySelector('[data-selected-variant]');
    if (variantJson) {
      variantJson.textContent = JSON.stringify(variant);
    }

    // Update footbar add to cart button state
    const footbarProduct = footbarPicker.closest('.js-footbar-product');
    if (footbarProduct) {
      const addToCartBtn = footbarProduct.querySelector('[data-js-product-button-add-to-cart]');
      if (addToCartBtn) {
        // Always remove loading state after variant update
        addToCartBtn.classList.remove('loading');
        if (variant.available) {
          addToCartBtn.removeAttribute('disabled');
          addToCartBtn.removeAttribute('data-button-status');
        } else {
          addToCartBtn.setAttribute('disabled', 'disabled');
          addToCartBtn.setAttribute('data-button-status', 'sold-out');
        }
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFootbarBridge);
  } else {
    // Give a small delay for other scripts to load
    setTimeout(initFootbarBridge, 100);
  }

  // Also initialize on theme ready if available
  if (typeof theme !== 'undefined' && theme.AssetsLoader) {
    theme.AssetsLoader.onPageLoaded(initFootbarBridge);
  }
})();
